import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/app/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarIcon, X, Plus } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { useSeo } from "@/lib/seo";

const DEFAULT_PROMPTS = [
  "Share your favorite memory",
  "What will you miss the most?",
  "Your wishes or advice",
  "A personal goodbye",
];

const THEMES = [
  { v: "celebration", l: "Celebration" },
  { v: "farewell", l: "Farewell" },
  { v: "wedding", l: "Wedding" },
  { v: "birthday", l: "Birthday" },
  { v: "retirement", l: "Retirement" },
  { v: "memorial", l: "Memorial" },
];

export default function CreateEvent() {
  useSeo({ title: "Create a new event", noindex: true, path: "/events/new" });
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [theme, setTheme] = useState("celebration");
  const [description, setDescription] = useState("");
  const [deadline, setDeadline] = useState(null);
  const [prompts, setPrompts] = useState([...DEFAULT_PROMPTS]);
  const [busy, setBusy] = useState(false);

  const updatePrompt = (i, v) => setPrompts((p) => p.map((x, idx) => idx === i ? v : x));
  const addPrompt = () => setPrompts((p) => [...p, "New prompt"]);
  const removePrompt = (i) => setPrompts((p) => p.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error("Add an event title"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/events", {
        title: title.trim(),
        theme,
        description,
        deadline: deadline ? deadline.toISOString() : null,
        prompts: prompts.filter((p) => p.trim()),
      });
      toast.success("Event created");
      navigate(`/events/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[var(--em-bg)]">
      <Header />
      <div className="max-w-3xl mx-auto px-6 md:px-10 pt-12 pb-28 md:pb-12">
        <p className="font-mono-label text-[var(--em-clay)]">New event</p>
        <h1 className="font-serif-em text-4xl md:text-5xl mt-3">Begin a memory.</h1>
        <p className="mt-2 text-[var(--em-ink-soft)]">A few details to set the stage — you can change everything later.</p>

        <form onSubmit={submit} className="mt-10 space-y-7">
          <div>
            <Label htmlFor="title" className="font-mono-label">Event title</Label>
            <Input id="title" data-testid="create-title-input" placeholder="Marilyn's Going Away Party" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-2 h-12 bg-white text-lg" />
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="font-mono-label">Theme</Label>
              <Select value={theme} onValueChange={setTheme}>
                <SelectTrigger data-testid="create-theme-select" className="mt-2 h-12 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {THEMES.map((t) => (<SelectItem key={t.v} value={t.v} data-testid={`theme-opt-${t.v}`}>{t.l}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-mono-label">Deadline (optional)</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button data-testid="create-deadline-btn" type="button" variant="outline" className="mt-2 h-12 w-full bg-white justify-start font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {deadline ? format(deadline, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={deadline} onSelect={setDeadline} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div>
            <Label htmlFor="desc" className="font-mono-label">Description</Label>
            <Textarea id="desc" data-testid="create-desc-input" placeholder="A short note shown to people you invite." value={description} onChange={(e) => setDescription(e.target.value)} className="mt-2 bg-white min-h-[100px]" />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="font-mono-label">Prompts for guests</Label>
              <Button type="button" variant="ghost" size="sm" data-testid="create-add-prompt-btn" onClick={addPrompt} className="text-[var(--em-clay)]"><Plus className="h-4 w-4 mr-1" /> Add prompt</Button>
            </div>
            <div className="mt-3 space-y-2">
              {prompts.map((p, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input data-testid={`create-prompt-input-${i}`} value={p} onChange={(e) => updatePrompt(i, e.target.value)} className="h-11 bg-white" />
                  <Button type="button" variant="ghost" size="icon" data-testid={`create-prompt-remove-${i}`} onClick={() => removePrompt(i)}><X className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-4 flex items-center gap-3">
            <Button data-testid="create-submit-btn" disabled={busy} className="h-12 px-7 rounded-full bg-[var(--em-clay)] hover:bg-[var(--em-clay-deep)] text-white btn-lift">
              {busy ? "Creating…" : "Create event"}
            </Button>
            <Button type="button" variant="ghost" onClick={() => navigate("/dashboard")} data-testid="create-cancel-btn">Cancel</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
